import qs from "qs";
import { REGION_NA, REGION_SA } from "../service/Constants";
/**
 * Maps groups of filters per region
 */
function mapRegionToModuleFilters(region) {
  switch (region) {
    case REGION_NA:
    case REGION_SA:
      return [
        {
          title: "Module combinations",
          filters: ["totalNumberOfModulesInArray"],
        },
        {
          title: "Release state",
          filters: ["release_state"],
        },
        {
          title: "Application",
          filters: ["moduleFamilyFilter_1"],
        },
        {
          title: "Series",
          filters: ["moduleFamilyFilter_2"],
        },
        {
          title: "Family",
          filters: ["moduleFamilyFilter_3"],
        },
        {
          title: "CCT",
          filters: ["cct_ref"],
        },
        {
          title: "CRI",
          filters: ["cri_ref"],
        },
        {
          title: "Spectrum",
          filters: ["special_spectrum"],
        },
        {
          title: "LES diameter",
          filters: ["lesDiameter"],
        },
        {
          title: "Length",
          filters: ["housingLength"],
        },
        {
          title: "Dimensions",
          filters: ["housingWidth", "housingHeight"],
        },
        {
          title: "Exceptions",
          filters: ["searchTermFilter"],
        },
      ];
    default: {
      return [
        {
          title: "Module combinations",
          filters: ["totalNumberOfModulesInArray"],
        },
        {
          title: "Release state",
          filters: ["release_state"],
        },
        {
          title: "Application",
          filters: ["moduleFamilyFilter_1"],
        },
        {
          title: "Series",
          filters: ["moduleFamilyFilter_2"],
        },
        {
          title: "Family",
          filters: ["moduleFamilyFilter_3"],
        },
        {
          title: "CCT",
          filters: ["cct_ref"],
        },
        {
          title: "CRI",
          filters: ["cri_ref"],
        },
        {
          title: "Spectrum",
          filters: ["special_spectrum"],
        },
        {
          title: "High/Low voltage",
          filters: ["moduleVoltageType"],
        },
        {
          title: "LES diameter",
          filters: ["lesDiameter"],
        },
        {
          title: "Length",
          filters: ["housingLength"],
        },
        {
          title: "Dimensions",
          filters: ["housingWidth", "housingHeight"],
        },
        {
          title: "Exceptions",
          filters: ["searchTermFilter"],
        },
      ];
    }
  }
}

/**
 *  Maps differences in filter types
 */
export const mapModuleFilterToType = {
  totalNumberOfModulesInArray: "select",
  housingWidth: "range",
  housingLength: "checkbox",
  housingHeight: "range",
  searchTermFilter: "search",
};

/**
 * Maps differences in filter name <=> api parameters relation
 */
export const mapModuleFilterToParameter = {
  moduleFamilyFilter_1: "productFamily",
  moduleFamilyFilter_2: "productFamily2",
  moduleFamilyFilter_3: "productFamily3",
  cct_ref: "cctRef",
  cri_ref: "criRef",
  special_spectrum: "specialSpectrum",
  release_state: "releaseState",
  searchTermFilter: "searchTerm",
  housingWidth: ["housingWidthMin", "housingWidthMax"],
  housingLength: "housingLength",
  housingHeight: ["housingHeightMin", "housingHeightMax"],
};

/**
 * Lookup filter <=> interface type relation
 * By default this is a 'Checkbox'
 * @param name
 * @returns {string}
 */
function lookupFilterToType(name) {
  return !mapModuleFilterToType[name]
    ? "checkbox"
    : mapModuleFilterToType[name];
}

/**
 * Lookup filter name <=> parameter name relation
 * By default this is the filter name
 * @param name
 * @returns String
 */
function lookupFilterToParameter(name) {
  return !mapModuleFilterToParameter[name]
    ? name
    : mapModuleFilterToParameter[name];
}

/**
 * Maps the filters against a users acl features
 * @param filters
 * @param aclFeatures
 * @returns {*}
 */
function mapModuleFiltersAcl(filters, aclFeatures) {
  if (aclFeatures) {
    // do nothing yet
  }
  return filters;
}

/**
 * Maps the filters to a region
 * @param region
 * @param input
 * @returns {*}
 */
function mapModuleFilters(region, input) {
  return [
    ...mapRegionToModuleFilters(region).map((group) => {
      return {
        title: group.title,
        filters: group.filters.map((name) => {
          const type = lookupFilterToType(name);
          const [filter] = input.filter((item) => item.name === name);

          const moduleFilter = {
            type,
            ...filter,
          };

          switch (name) {
            case "housingWidth":
              moduleFilter.displayName = "Housing width";
              break;
            case "housingHeight":
              moduleFilter.displayName = "Housing height";
              break;
            default:
              moduleFilter.displayName = name;
              break;
          }

          return moduleFilter;
        }),
      };
    }),
  ];
}

/**
 * Maps the filters to API parameters
 * @param filters
 * @param moduleProps
 * @returns {*}
 */
function mapModuleFilterState({
  filters,
  inputLock,
  moduleFlux,
  moduleCurrent,
  moduleTemperature,
  luminaireFlux,
  lor,
  moduleSearchTerm,
  inputLockRTH,
  moduleTa,
  moduleRTH,
  canRTH,
}) {
  const parameters = {};
  //Checking what parameters needs to be send with it.
  if (inputLock === "flux") {
    parameters["targetFlux"] = moduleFlux;
    if (luminaireFlux) {
      parameters["luminaireLumens"] = luminaireFlux;
    }
  } else if (inputLock === "current") {
    if (moduleCurrent) {
      parameters["targetCurrent"] = moduleCurrent / 1000;
    }
  }

  if (canRTH) {
    if (moduleTa) {
      parameters["tAmbient"] = moduleTa;
    }
    if (inputLockRTH === "tc") {
      if (moduleTemperature) {
        parameters["temperature"] = moduleTemperature;
      }
    } else if (inputLockRTH === "rth") {
      if (moduleRTH) {
        parameters["rth"] = moduleRTH;
      }
    }
  } else {
    // if Temperature is present
    if (moduleTemperature) {
      parameters["temperature"] = moduleTemperature;
    }
  }

  if (lor) {
    parameters["luminaireLor"] = lor;
  }

  filters.forEach((group) => {
    group.filters.forEach((filter) => {
      const paramName = lookupFilterToParameter(filter.name);
      if (lookupFilterToType(filter.name) === "range") {
        //So if it is an range filter it will have a min and max property
        if (filter.value) {
          //if value is single value then...
          if (filter.value.min && filter.value.max) {
            parameters[paramName[0]] = filter.value.min;
            parameters[paramName[1]] = filter.value.max;
          } else {
            parameters[paramName[0]] = filter.from;
            parameters[paramName[1]] = filter.to;
          }
        }
      } else {
        let filterValue = null;
        if (filter.value != null) {
          // Prevent setting a filter value that the module doesn't have.
          if (Array.isArray(filter.value)) {
            filterValue = [];
            filter.value.map((value) => {
              if (
                filter.availableValues.some(
                  (availableValue) => availableValue.value === value
                )
              ) {
                filterValue.push(value);
              }
            });
          } else {
            filterValue = filter.value;
          }
          parameters[paramName] = filterValue;
        }
      }
    });
  });

  //Set this after all the filters are applied cause then the most recent searchTerm will be send to the backend and saved
  //If you put this before the 'Add filters forEach loop' then the saved searchTerm value will be added
  if (moduleSearchTerm) {
    parameters["searchTerm"] = moduleSearchTerm;
  } else {
    if (moduleSearchTerm === "") {
      parameters["searchTerm"] = "";
    }
  }
  return parameters;
}

function mapModuleFilterQueryParameters(filters) {
  return (
    "?" +
    qs.stringify(
      filters.reduce((params, filter) => {
        params[filter.field] = filter.value;
        return params;
      }, {})
    )
  );
}

export {
  mapModuleFilters,
  mapModuleFilterState,
  mapModuleFilterQueryParameters,
  mapModuleFiltersAcl,
};
